import type { AsyncState } from '../../platform/async/Async'
import { accountHealth, type Health } from './accountHealth'
import type { Account } from './useAccounts'
import type { ProviderItem } from './useAggregator'

/**
 * A group's status IS a per-account `Health`. There is no group-only kind:
 * "Disconnected" is a fact `connection_status` states about an account, and a
 * group is read off its accounts (`healthFor`), so an item that is absent, an
 * item that is present with `status: "removed"`, and an item that is simply
 * gone all read the same way — by what the backend says, not by what the
 * client could or could not join.
 */
export type GroupHealth = Health

export type InstitutionGroup = {
  key: string
  /** `orphan` now means ONLY "no resolvable `ProviderItem`, so no item-level
   *  action (sync, reauth, disconnect)". It does NOT imply Disconnected —
   *  that comes from `connection_status` through `healthFor`. Kept distinct
   *  from `manual` so nothing claims "you update this yourself" about a
   *  connection the app cannot resolve. */
  kind: 'linked' | 'manual' | 'orphan'
  item: ProviderItem | null
  name: string
  logoUrl: string | null
  accounts: Account[]
}

/**
 * One row per LINKED institution — grouped by `itemId`, joined to
 * `useProviderItems()` (#425 §A3's own instruction). The Manual bucket is
 * deliberately NOT built here — see `InstitutionsPanel`'s own docblock for
 * why it lives outside this surface entirely.
 *
 * **Keyed on `provider !== 'manual'`, not `itemId !== null`.** The two read
 * the same for every account this app can create today, but they are not
 * the same CLAIM: `itemId` is a join key an aggregator sync populates,
 * `provider` is the field `accountHealth()`/`toAccount()` already treat as
 * the one source of truth for "is this manual" (`useAccounts.ts:102` —
 * `toAccount()` copies the wire's `provider` straight through, no
 * derivation — and `accountHealth.ts:57`'s own `provider === 'manual'`
 * check). Nothing in `toAccount()` enforces that a linked
 * account's `itemId` is always non-null — that was an invariant this file
 * asserted about the BACKEND rather than one the client checks, and a
 * `provider !== 'manual'` account with a null `itemId` used to land, mis-
 * labelled, in the Manual bucket (a real gap: `rowHealth()` could then read
 * `reauth`/`stale` off it and render that inside a row captioned "Manual").
 * Keying on `provider` instead makes that shape land in `orphan`, which
 * neither claims "you update this yourself" nor is folded into a linked
 * institution it does not belong to.
 */
export function linkedGroupsOf(accounts: Account[], items: ProviderItem[]): InstitutionGroup[] {
  const byKey = new Map<string, Account[]>()
  for (const a of accounts) {
    if (a.provider === 'manual') continue
    // A real `itemId` groups by the item, as intended. Without one, a
    // SHARED sentinel here would merge every orphan account into one row —
    // two accounts from two different institutions, both mid-sync, folded
    // into a single row captioned with only one of the two names
    // (architect review, #425 T2 re-gate, non-blocking). `institutionName`
    // groups same-institution orphans together instead; a null name too
    // (nothing left to group by) falls back to the account's own id, which
    // keeps it its own row rather than merging with an unrelated one that
    // also happens to have no name.
    //
    // **Trades away the old key's collision guarantee, deliberately.** A
    // real `itemId` (a UUID), an `institutionName` (free text) and an
    // account `id` (a UUID) now share one key space with no format keeping
    // them apart the way a dedicated sentinel did — accepted because a name
    // colliding with a UUID is not reachable in practice, and the row-merge
    // defect this replaces was real. **Second-order effect, not pinned by
    // any test:** an orphan account and a genuinely linked item can share
    // the same `institutionName` (an account mid-relink alongside its own
    // freshly-linked replacement, say) and render as TWO rows both
    // captioned "Royal Bank of Canada" — arguably correct, since one really
    // is a different connection state from the other, but nothing states
    // that choice or guards it.
    const key = a.itemId ?? a.institutionName ?? a.id
    const list = byKey.get(key)
    if (list) list.push(a)
    else byKey.set(key, [a])
  }

  const groups: InstitutionGroup[] = []
  for (const item of items) {
    const itemAccounts = byKey.get(item.id) ?? []
    byKey.delete(item.id)
    groups.push({
      key: item.id,
      kind: 'linked',
      item,
      name: item.institutionName ?? itemAccounts[0]?.institutionName ?? 'Connection',
      logoUrl: item.institutionLogoUrl,
      accounts: itemAccounts,
    })
  }
  // Whatever is left — a real itemId with no matching item, or no itemId at
  // all — is kept, not dropped, and never mistaken for a healthy `linked`
  // row (see `kind: 'orphan'` above).
  for (const [key, orphanAccounts] of byKey) {
    groups.push({
      key: `orphan:${key}`,
      kind: 'orphan',
      item: null,
      name: orphanAccounts[0]?.institutionName ?? 'Connection',
      logoUrl: null,
      accounts: orphanAccounts,
    })
  }
  return groups
}

/**
 * The ONE derivation of a group's status — `Connections.tsx`,
 * `InstitutionsPanel.tsx`, the tile and the card all call this.
 *
 * `manual` stays `manual`. Every other group is read off its FIRST account
 * through `accountHealth()` (they share an item, so they share its facts).
 * **No branch reads `item.status` or an item's absence** — `connection_status`
 * is the single source, which is what lets a provider-removed item that the
 * items endpoint still lists read Disconnected, and a dangling row with a
 * null `connection_status` read its own health. `null` when the group has no
 * account yet (mid first sync).
 */
export function healthFor(group: InstitutionGroup): GroupHealth | null {
  if (group.kind === 'manual') return { kind: 'manual' }
  const first = group.accounts[0]
  return first ? accountHealth(first) : null
}

/** Counts as a live connection: not disconnected. The Connections tile's
 *  count and `isSyncable` below both read this one predicate. */
export function isLive(group: InstitutionGroup): boolean {
  return healthFor(group)?.kind !== 'disconnected'
}

/** Should this connection be offered a sync — per-item "Sync now" and "Sync
 *  all" both read this. A disconnected group is not: a provider-removed
 *  (Gingham) item is still LISTED by the items endpoint, but the backend
 *  answers a sync of it `410 {status: "removed"}`. No item, nothing to sync. */
export function isSyncable(group: InstitutionGroup): boolean {
  return group.item !== null && isLive(group)
}

/**
 * The items "Sync all" fans out to, or `null` when it cannot be decided.
 *
 * **`null` while either list is unknown — loading OR errored — and the caller
 * hides "Sync all".** Deliberate: which items are disconnected is only known
 * from the accounts (`connection_status`), and a sync of a provider-removed
 * item is answered `410`, so guessing from the items list alone would fan out
 * to exactly the connections that cannot sync. Failing closed costs a hidden
 * button until the accounts load, which the page is already waiting on; failing
 * open costs a failed request and a misleading toast. `[]` is different: known,
 * and nothing to sync.
 */
export function syncTargetsOf(
  accounts: Account[] | null,
  items: ProviderItem[] | null,
): ProviderItem[] | null {
  if (accounts === null || items === null) return null
  return linkedGroupsOf(accounts, items).flatMap((g) => (isSyncable(g) && g.item ? [g.item] : []))
}

/** The id to send as `relink_item_id`: the disconnected account's own `itemId`
 *  from `GET /accounts` — never looked up in the items list, where a
 *  user-disconnected item is absent. `null` unless the group is disconnected
 *  AND reconnectable. */
export function relinkIdOf(group: InstitutionGroup): string | null {
  const health = healthFor(group)
  if (health?.kind !== 'disconnected' || !health.reconnectable) return null
  return group.accounts[0]?.itemId ?? null
}

/**
 * The banks "Link a bank" offers to RECONNECT first: every group that is
 * disconnected AND reconnectable, one per relink id, in the order the groups
 * come. The web cannot know which bank the user is about to pick inside the
 * bank window, so it offers them all and lets the user choose — or link a
 * different bank. An institution's identity here is the group's `name` (the
 * accounts' `institution_name`); accounts carry no `institution_id`.
 */
export function reconnectOffersOf(accounts: Account[], items: ProviderItem[]): InstitutionGroup[] {
  const seen = new Set<string>()
  return linkedGroupsOf(accounts, items).filter((g) => {
    const id = relinkIdOf(g)
    if (id === null || seen.has(id)) return false
    seen.add(id)
    return true
  })
}

/**
 * The ONE "is there anything to show" decision for the items-backed surfaces
 * (the Connections tile, the Institutions panel and the `/connections` screen). `useProviderItems()`
 * reports `empty` when `items` is `[]`, but orphan groups exist without any
 * item — so emptiness must be judged on the joined GROUPS, not on `items`.
 * An `empty` state with at least one orphan group (or `alsoShows`, e.g. the
 * panel's Manual row) becomes a success with no items, which lets the
 * caller's render prop run and `linkedGroupsOf` surface the orphans.
 */
export function emptyUnlessGroups(
  state: AsyncState<ProviderItem[]>,
  accounts: Account[],
  alsoShows = false,
): AsyncState<ProviderItem[]> {
  if (state.status !== 'empty') return state
  return alsoShows || linkedGroupsOf(accounts, []).length > 0
    ? { status: 'success', data: [] }
    : state
}

/**
 * How the items query and the accounts query combine into the ONE state the
 * `/connections` list is rendered from. Pure, so the whole items x accounts
 * table is unit-tested rather than sampled through the route.
 *
 * Only an `empty` items list needs the accounts query, because orphan groups
 * exist without any item. Until accounts resolves, an empty items list cannot
 * be called empty: while accounts is `loading` the result is `loading`, and
 * when accounts has `error`ed the result is a `success` with no items, which
 * hands the screen to its nested accounts `<Async>` to report the real error
 * instead of the false "No connected banks yet.". Any non-empty items state
 * passes through unchanged.
 */
export function connectionsListState(
  items: AsyncState<ProviderItem[]>,
  accounts: AsyncState<Account[]>,
): AsyncState<ProviderItem[]> {
  if (items.status !== 'empty') return items
  switch (accounts.status) {
    case 'success':
    case 'stale':
      return emptyUnlessGroups(items, accounts.data)
    case 'empty':
      return items
    case 'loading':
      return accounts
    case 'error':
      return { status: 'success', data: [] }
  }
}
